/**
 * Viewer Presence Service
 * Handles tracking and retrieving active viewers for projects
 */

import { API_BASE_URL } from '../API/api';
import { getIdToken, getUserEmail } from '../utils/authSession';

class ViewerPresenceService {
  /**
   * Send heartbeat to indicate user is actively viewing a project
   * @param {number} projectId - Project ID
   * @param {string} userEmail - User email (optional, will be extracted from token if not provided)
   * @returns {Promise<Object>} Response from server
   */
  async sendHeartbeat(projectId, userEmail = null) {
    try {
      // Ensure projectId is a number
      const numericProjectId = typeof projectId === 'string' ? parseInt(projectId, 10) : projectId;
      console.log(`💓 ViewerPresenceService: Sending heartbeat for project ${numericProjectId} (original: ${projectId}, type: ${typeof projectId})`);
      
      // Get user email from token if not provided
      if (!userEmail) {
        try {
          userEmail = await getUserEmail();
          if (userEmail) console.log(`💓 ViewerPresenceService: Got email from token: ${userEmail}`);
        } catch (error) {
          console.warn('⚠️ ViewerPresenceService: Could not get user email from token, using sessionStorage:', error);
          userEmail = sessionStorage.getItem('userEmail');
          console.log(`💓 ViewerPresenceService: Got email from sessionStorage: ${userEmail}`);
        }
      }

      if (!userEmail) {
        console.warn('⚠️ ViewerPresenceService: No user email available for heartbeat');
        return { success: false, error: 'User email not available' };
      }

      // Get auth token for request
      let authToken = null;
      try {
        authToken = await getIdToken();
      } catch (error) {
        console.warn('Could not get auth token:', error);
      }

      const headers = {
        'Content-Type': 'application/json',
      };

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const url = `${API_BASE_URL}/api/projects/${numericProjectId}/viewers/heartbeat`;
      console.log(`💓 ViewerPresenceService: Sending POST to ${url} with email: ${userEmail}`);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ email: userEmail }),
      });

      console.log(`💓 ViewerPresenceService: Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ ViewerPresenceService: HTTP ${response.status}: ${errorText}`);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`💓 ViewerPresenceService: Heartbeat response:`, data);
      return data;
    } catch (error) {
      console.error('❌ Error sending viewer heartbeat:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get list of active viewers for a project
   * @param {number} projectId - Project ID
   * @returns {Promise<Array<string>>} Array of user emails
   */
  async getActiveViewers(projectId) {
    try {
      // Ensure projectId is a number
      const numericProjectId = typeof projectId === 'string' ? parseInt(projectId, 10) : projectId;
      const url = `${API_BASE_URL}/api/projects/${numericProjectId}/viewers`;
      console.log(`👥 ViewerPresenceService: Fetching active viewers from ${url} (projectId: ${projectId}, numeric: ${numericProjectId})`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log(`👥 ViewerPresenceService: Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ ViewerPresenceService: HTTP ${response.status}: ${errorText}`);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`👥 ViewerPresenceService: Active viewers response:`, data);
      return data.viewers || [];
    } catch (error) {
      console.error('❌ Error getting active viewers:', error);
      return [];
    }
  }

  /**
   * Register that user clicked the 3D overlay for a project
   * This is required (along with heartbeats) to show user as active
   * @param {number} projectId - Project ID
   * @param {string} userEmail - User email (optional, will be extracted from token if not provided)
   * @returns {Promise<Object>} Response from server
   */
  async registerOverlayClick(projectId, userEmail = null) {
    try {
      // Ensure projectId is a number
      const numericProjectId = typeof projectId === 'string' ? parseInt(projectId, 10) : projectId;
      console.log(`🎯 ViewerPresenceService: Registering overlay click for project ${numericProjectId} (original: ${projectId})`);
      
      // Get user email from token if not provided
      if (!userEmail) {
        try {
          userEmail = await getUserEmail();
        } catch (error) {
          console.warn('⚠️ ViewerPresenceService: Could not get user email from token, using sessionStorage:', error);
          userEmail = sessionStorage.getItem('userEmail');
        }
      }

      if (!userEmail) {
        console.warn('⚠️ ViewerPresenceService: No user email available for overlay click');
        return { success: false, error: 'User email not available' };
      }

      // Get auth token for request
      let authToken = null;
      try {
        authToken = await getIdToken();
      } catch (error) {
        console.warn('Could not get auth token:', error);
      }

      const headers = {
        'Content-Type': 'application/json',
      };

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const url = `${API_BASE_URL}/api/projects/${numericProjectId}/viewers/overlay-click`;
      console.log(`🎯 ViewerPresenceService: Sending POST to ${url} with email: ${userEmail}`);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ email: userEmail }),
      });

      console.log(`🎯 ViewerPresenceService: Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ ViewerPresenceService: HTTP ${response.status}: ${errorText}`);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`🎯 ViewerPresenceService: Overlay click response:`, data);
      return data;
    } catch (error) {
      console.error('❌ Error registering overlay click:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Notify server that user has left the project view
   * @param {number} projectId - Project ID
   * @param {string} userEmail - User email (optional)
   * @returns {Promise<Object>} Response from server
   */
  async leaveProjectView(projectId, userEmail = null) {
    try {
      // Ensure projectId is a number
      const numericProjectId = typeof projectId === 'string' ? parseInt(projectId, 10) : projectId;
      
      // Get user email from token if not provided
      if (!userEmail) {
        try {
          userEmail = await getUserEmail();
        } catch (error) {
          console.warn('Could not get user email from token:', error);
          userEmail = sessionStorage.getItem('userEmail');
        }
      }

      if (!userEmail) {
        console.warn('⚠️ No user email available for leave notification');
        return { success: false, error: 'User email not available' };
      }

      // Get auth token for request
      let authToken = null;
      try {
        authToken = await getIdToken();
      } catch (error) {
        console.warn('Could not get auth token:', error);
      }

      const headers = {
        'Content-Type': 'application/json',
      };

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/projects/${numericProjectId}/viewers/leave`,
        {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({ email: userEmail }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Error leaving project view:', error);
      return { success: false, error: error.message };
    }
  }
}

// Export singleton instance
const viewerPresenceService = new ViewerPresenceService();
export default viewerPresenceService;

